import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, PencilIcon, TrashIcon, CalendarIcon, MapPinIcon, CurrencyEuroIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { eventApi, businessApi, staffApi } from '../services/api';
import { Event, Business } from '../types';
import { Modal } from '../components/Modal';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface EventForm {
  businessId: string;
  title: string;
  description: string;
  startDate: string;
  startHour: string;
  endDate: string;
  endHour: string;
  maxCapacity: number;
  staffId: string;
  price: number;
  location: string;
  isPublic: boolean;
  isFree: boolean;
}

export function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<EventForm>({
    defaultValues: { maxCapacity: 20, description: '', staffId: '', price: 0, location: '', isPublic: true, isFree: true, startHour: '09:00', endHour: '10:00' },
  });

  const isFree = watch('isFree', true);
  const watchedBusiness = watch('businessId');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (watchedBusiness) loadStaff(watchedBusiness);
  }, [watchedBusiness]);

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const bList = bRes.data.data.businesses || [];
      setBusinesses(bList);
      if (bList.length > 0) {
        const eRes = await eventApi.getBusinessEvents(bList[0].id);
        setEvents(eRes.data.data.events || []);
        await loadStaff(bList[0].id);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setIsLoading(false); }
  };

  const loadStaff = async (businessId: string) => {
    try {
      const r = await staffApi.getBusinessStaff(businessId);
      setStaff(r.data.data.staff || []);
    } catch {}
  };

  const handleImageChange = (file: File) => {
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = e => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const uploadEventImage = async (eventId: string) => {
    if (!imageFile) return;
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('image', imageFile);
      await axios.post(`${API_URL}/upload/event/${eventId}/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
    } catch { toast.error('Error subiendo imagen'); }
    finally { setUploadingImage(false); }
  };

  const onSubmit = async (data: EventForm) => {
    try {
      const startTime = new Date(`${data.startDate}T${data.startHour}:00`);
      const endTime = new Date(`${data.endDate}T${data.endHour}:00`);
      const payload = {
        businessId: data.businessId,
        title: data.title,
        description: data.description || undefined,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        maxCapacity: Number(data.maxCapacity),
        staffId: data.staffId || undefined,
        price: data.isFree ? 0 : Number(data.price),
        location: data.location || undefined,
        isPublic: data.isPublic,
      };

      let eventId: string;
      if (editingEvent) {
        await eventApi.updateEvent(editingEvent.id, payload as any);
        eventId = editingEvent.id;
        toast.success('Evento actualizado');
      } else {
        const r = await eventApi.createEvent(payload as any);
        eventId = r.data.data.event.id;
        toast.success('Evento publicado 🎉');
      }

      if (imageFile) await uploadEventImage(eventId);

      setIsModalOpen(false);
      setEditingEvent(null);
      setImageFile(null);
      setImagePreview(null);
      reset();
      fetchData();
    } catch { toast.error('Error al guardar el evento'); }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setImagePreview((event as any).image || null);
    setImageFile(null);
    const price = (event as any).price || 0;
    const sd = new Date(event.startTime);
    const ed = new Date(event.endTime);
    const pad = (n: number) => String(n).padStart(2,'0');
    reset({
      businessId: event.businessId,
      title: event.title,
      description: event.description || '',
      startDate: sd.toISOString().slice(0,10),
      startHour: `${pad(sd.getHours())}:${pad(Math.floor(sd.getMinutes()/15)*15).padStart(2,'0')}`,
      endDate: ed.toISOString().slice(0,10),
      endHour: `${pad(ed.getHours())}:${pad(Math.floor(ed.getMinutes()/15)*15).padStart(2,'0')}`,
      maxCapacity: event.maxCapacity || 20,
      staffId: (event as any).staffId || '',
      price: price,
      location: (event as any).location || '',
      isPublic: (event as any).isPublic !== false,
      isFree: !price || price == 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este evento?')) return;
    try {
      await eventApi.deleteEvent(id);
      toast.success('Evento eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddNew = () => {
    setEditingEvent(null);
    setImageFile(null);
    setImagePreview(null);
    const today = new Date().toISOString().slice(0,10);
    reset({
      businessId: businesses[0]?.id || '',
      title: '', description: '',
      startDate: today, startHour: '09:00',
      endDate: today, endHour: '10:00',
      maxCapacity: 20, staffId: '',
      price: 0, location: '',
      isPublic: true, isFree: true,
    });
    setIsModalOpen(true);
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .ev-img { width: 100%; height: 160px; object-fit: cover; border-radius: 14px 14px 0 0; }
        .ev-no-img { width: 100%; height: 100px; background: linear-gradient(135deg,#1e293b,#334155); border-radius: 14px 14px 0 0; display: flex; align-items: center; justify-content: center; font-size: 2.5rem; }
        .img-drop { border: 2px dashed #e2e8f0; border-radius: 12px; padding: 28px; text-align: center; cursor: pointer; transition: all .2s; background: #f8fafc; }
        .img-drop:hover { border-color: #3b82f6; background: #eff6ff; }
        .img-preview { width: 100%; height: 180px; object-fit: cover; border-radius: 12px; margin-bottom: 8px; }
        .price-badge { display: inline-flex; align-items: center; gap: 4px; font-size: .72rem; font-weight: 700; padding: 3px 10px; border-radius: 100px; }
        .price-free { background: #dcfce7; color: #15803d; }
        .price-paid { background: #dbeafe; color: #1d4ed8; }
        .toggle-free { display: flex; gap: 8px; }
        .toggle-btn { flex: 1; padding: 10px; border-radius: 10px; border: 2px solid #e2e8f0; background: white; font-weight: 600; font-size: .85rem; cursor: pointer; transition: all .2s; }
        .toggle-btn.active-free { background: #dcfce7; border-color: #86efac; color: #15803d; }
        .toggle-btn.active-paid { background: #dbeafe; border-color: #93c5fd; color: #1d4ed8; }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Eventos</h1>
          <p className="text-secondary-500 text-sm mt-1">Publica clases, actividades, talleres y promociones</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-5 w-5"/>Nuevo evento
        </button>
      </div>

      {events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => {
            const img = (event as any).image;
            const price = (event as any).price;
            const location = (event as any).location;
            const d = new Date(event.startTime);
            return (
              <div key={event.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', transition: 'transform .2s, box-shadow .2s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,.08)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}>
                {img ? <img src={img} alt={event.title} className="ev-img"/> : <div className="ev-no-img">📅</div>}
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <h3 style={{ fontSize: '.95rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>{event.title}</h3>
                    <span className={`price-badge ${!price || price == 0 ? 'price-free' : 'price-paid'}`}>
                      {!price || price == 0 ? '🆓 Gratis' : `💶 ${price}€`}
                    </span>
                  </div>
                  {event.description && (
                    <p style={{ fontSize: '.8rem', color: '#64748b', marginBottom: 10, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {event.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#64748b' }}>
                      <CalendarIcon style={{ width: 14, flexShrink: 0 }}/>
                      {d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })} · {d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' })}
                    </div>
                    {location && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#64748b' }}>
                        <MapPinIcon style={{ width: 14, flexShrink: 0 }}/>{location}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#64748b' }}>
                      👥 {event.maxCapacity} plazas
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleEdit(event)} className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1">
                      <PencilIcon className="h-4 w-4"/>Editar
                    </button>
                    <button onClick={() => handleDelete(event.id)} style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', color: '#ef4444', border: 'none', cursor: 'pointer' }}>
                      <TrashIcon className="h-4 w-4"/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{ fontSize: '4rem', marginBottom: 16 }}>📅</div>
          <p className="text-secondary-500 font-medium mb-2">No tienes eventos publicados</p>
          <p className="text-secondary-400 text-sm mb-6">Crea tu primer evento para que tus clientes puedan inscribirse</p>
          <button onClick={handleAddNew} className="btn-primary inline-flex items-center gap-2">
            <PlusIcon className="h-5 w-5"/>Crear primer evento
          </button>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingEvent(null); setImageFile(null); setImagePreview(null); }} title={editingEvent ? 'Editar evento' : 'Nuevo evento'} maxWidth="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* IMAGEN */}
          <div>
            <label className="label mb-2">Imagen del evento</label>
            {imagePreview ? (
              <div style={{ position: 'relative' }}>
                <img src={imagePreview} className="img-preview" alt="preview"/>
                <button type="button" onClick={() => { setImagePreview(null); setImageFile(null); }}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(239,68,68,.9)', color: 'white', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer' }}>✕</button>
                <button type="button" onClick={() => imageInputRef.current?.click()}
                  style={{ position: 'absolute', bottom: 16, right: 8, background: 'rgba(0,0,0,.6)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 600 }}>
                  📷 Cambiar
                </button>
              </div>
            ) : (
              <div className="img-drop" onClick={() => imageInputRef.current?.click()}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>🖼️</div>
                <p style={{ fontSize: '.85rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>Subir imagen del evento</p>
                <p style={{ fontSize: '.75rem', color: '#94a3b8' }}>JPG, PNG · Máx 5MB · Recomendado 1200×630px</p>
              </div>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && handleImageChange(e.target.files[0])}/>
          </div>

          {!editingEvent && businesses.length > 1 && (
            <div>
              <label className="label">Negocio *</label>
              <select {...register('businessId')} className="input">
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Título del evento *</label>
            <input {...register('title', { required: 'El título es obligatorio' })} className="input" placeholder="Ej: Clase de yoga para principiantes, Open day CrossFit..." />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="label">Descripción</label>
            <textarea {...register('description')} className="input" rows={3} placeholder="Describe el evento: qué incluye, nivel requerido, qué traer..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Fecha inicio *</label>
              <input {...register('startDate', { required: 'Obligatorio' })} type="date" className="input"/>
            </div>
            <div>
              <label className="label">Hora inicio *</label>
              <select {...register('startHour', { required: 'Obligatorio' })} className="input">
                {Array.from({length: 24}, (_, h) => (
                  ['00','15','30','45'].map(m => (
                    <option key={`${h}:${m}`} value={`${String(h).padStart(2,'0')}:${m}`}>
                      {String(h).padStart(2,'0')}:{m}
                    </option>
                  ))
                ))}
              </select>
            </div>
            <div>
              <label className="label">Fecha fin *</label>
              <input {...register('endDate', { required: 'Obligatorio' })} type="date" className="input"/>
            </div>
            <div>
              <label className="label">Hora fin *</label>
              <select {...register('endHour', { required: 'Obligatorio' })} className="input">
                {Array.from({length: 24}, (_, h) => (
                  ['00','15','30','45'].map(m => (
                    <option key={`${h}:${m}`} value={`${String(h).padStart(2,'0')}:${m}`}>
                      {String(h).padStart(2,'0')}:{m}
                    </option>
                  ))
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Plazas máximas</label>
              <input {...register('maxCapacity', { valueAsNumber: true })} type="number" min={1} className="input"/>
            </div>
            <div>
              <label className="label">Ubicación</label>
              <input {...register('location')} className="input" placeholder="Sala principal, Parque del Retiro..."/>
            </div>
          </div>

          {/* PRECIO */}
          <div>
            <label className="label mb-2">¿El evento es gratuito o de pago?</label>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              <button type="button"
                style={{flex:1,padding:'12px',borderRadius:10,border:'2px solid',fontWeight:700,fontSize:'.88rem',cursor:'pointer',transition:'all .2s',
                  background:isFree?'#dcfce7':'white',borderColor:isFree?'#86efac':'#e2e8f0',color:isFree?'#15803d':'#64748b'}}
                onClick={() => { reset({ ...watch(), isFree: true, price: 0 }); }}>
                🆓 Gratis
              </button>
              <button type="button"
                style={{flex:1,padding:'12px',borderRadius:10,border:'2px solid',fontWeight:700,fontSize:'.88rem',cursor:'pointer',transition:'all .2s',
                  background:!isFree?'#dbeafe':'white',borderColor:!isFree?'#93c5fd':'#e2e8f0',color:!isFree?'#1d4ed8':'#64748b'}}
                onClick={() => { reset({ ...watch(), isFree: false }); }}>
                💶 De pago
              </button>
            </div>
            {!isFree && (
              <div>
                <label className="label">Precio por persona (€) *</label>
                <div style={{position:'relative'}}>
                  <span style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'#64748b',fontWeight:600}}>€</span>
                  <input {...register('price', { valueAsNumber: true, min: 0.01 })} type="number" min={0.01} step="0.01" className="input" placeholder="15.00" style={{paddingLeft:28}}/>
                </div>
              </div>
            )}
          </div>

          {staff.length > 0 && (
            <div>
              <label className="label">Responsable / Instructor</label>
              <select {...register('staffId')} className="input">
                <option value="">— Sin asignar —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input {...register('isPublic')} type="checkbox" id="isPublic" defaultChecked style={{ width: 16, height: 16 }}/>
            <label htmlFor="isPublic" style={{ fontSize: '.85rem', color: '#475569', fontWeight: 500 }}>
              Evento público (visible en la página del negocio)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setIsModalOpen(false); setEditingEvent(null); setImageFile(null); setImagePreview(null); }} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={isSubmitting || uploadingImage} className="btn-primary">
              {isSubmitting || uploadingImage ? 'Guardando...' : editingEvent ? 'Actualizar evento' : '🚀 Publicar evento'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
